/**
 * calculator_controller.js — every trade calculator page
 * (templates/tools/calculator_base.html, /tools/<slug>).
 *
 * The page arrives already answered by the server. This controller makes it
 * answer as you type: it reads the form, runs the calculator's browser mirror
 * (_calculators.js, held to the Python formula by
 * tests/fixtures/calculator_cases.json), and redraws the figures, warnings
 * and the "Turn this into a quote" link. It also keeps the URL in step, so the
 * address bar is always a link to this exact answer.
 *
 * It validates nothing itself: the inputs carry the spec's min, max and step,
 * and the browser's own constraint check says whether a box can be used. A box
 * that cannot blanks the answer rather than letting a half-typed number print
 * a wrong one.
 *
 * The assistant bar (tools/_ai_bar.html) posts the described job and the
 * current values to the page's /ai endpoint and writes back only the fields it
 * returns; the server has already validated them against the same spec.
 */

import { Controller } from "@hotwired/stimulus";
import { formatFigure } from "./_calc_math.js";
import { FORMULAS } from "./_calculators.js";
import { formatMoney } from "./_display_format.js";

export default class extends Controller {
    static targets = ["form", "field", "error", "answer", "notice", "warnings", "output",
                      "handoff", "peek", "aiInput", "aiSend", "aiLog", "aiStatus"];
    static values = { slug: String, aiUrl: String, quoteUrl: String, csrf: String };

    connect() {
        this._formula = FORMULAS[this.slugValue];
        this._session = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID() : String(Date.now());
        this._history = [];
        try {
            this._money = JSON.parse(this.element.dataset.displayFormat || "null");
        } catch (_error) {
            this._money = null;
        }
        this._watchAnswer();
    }

    disconnect() {
        if (this._answerObserver) this._answerObserver.disconnect();
    }

    /** Show the peek bar only while the answer card is below the screen: above
        it someone is filling in the boxes, past it they are reading on. The
        stylesheet keeps the bar off wide screens, where the answer sits beside
        the boxes. */
    _watchAnswer() {
        if (!this.hasPeekTarget || !("IntersectionObserver" in window)) return;
        this._answerObserver = new IntersectionObserver(([entry]) => {
            this.peekTarget.hidden = entry.isIntersecting || entry.boundingClientRect.top < 0;
        });
        this._answerObserver.observe(this.answerTarget);
    }

    // --- answering -----------------------------------------------------------

    recalculate() {
        this._toggleFields();
        if (!this._formula) return;   // no mirror: the submit button still asks the server
        const values = this._values();
        this._syncUrl();
        if (values === null) {
            this._show(null);
            return;
        }
        this._show(this._formula(values));
    }

    /** With JS on, the button is "show me": answer here and bring it into view. */
    submit(event) {
        if (!this._formula) return;
        event.preventDefault();
        this.recalculate();
        this.answerTarget.focus({ preventScroll: true });
        this.answerTarget.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    /** The form's values as the formula takes them, or null if a box cannot be used. */
    _values() {
        let usable = true;
        const values = {};
        this.formTarget.querySelectorAll("input, select").forEach((el) => {
            if (el.type === "radio") {
                if (el.checked) values[el.name] = el.value;
            } else if (el.type === "number") {
                const ok = el.value.trim() !== "" && el.checkValidity();
                this._markError(el, ok ? "" : "Enter a number in range.");
                usable = usable && (ok || el.closest("[hidden]") !== null);
                values[el.name] = Number(el.value);
            } else {
                values[el.name] = el.value;
            }
        });
        return usable ? values : null;
    }

    _markError(input, message) {
        const field = input.closest("[data-calculator-target~='field']");
        field.classList.toggle("has-error", Boolean(message));
        field.querySelector("[data-calculator-target~='error']").textContent = message;
    }

    _show(result) {
        this.noticeTarget.hidden = result !== null;
        this.outputTargets.forEach((el) => {
            const value = result ? result.outputs[el.dataset.key] : null;
            el.textContent = this._figure(el, value);
        });
        const warnings = result ? result.warnings : [];
        this.warningsTarget.replaceChildren(...warnings.map((text) => {
            const li = document.createElement("li");
            li.textContent = text;
            return li;
        }));
        this.warningsTarget.hidden = warnings.length === 0;
        this._showHandoff(result ? result.items : []);
    }

    _figure(el, value) {
        if (value === null || value === undefined) return "";
        if ("money" in el.dataset && this._money) return formatMoney(String(value), this._money);
        const unit = el.dataset.unit ? ` ${el.dataset.unit}` : "";
        return `${formatFigure(value, Number(el.dataset.places))}${unit}`;
    }

    /** The quote generator with these materials on it, the contract DraftDocumentSchema.items_from_link reads. */
    _showHandoff(items) {
        this.handoffTarget.hidden = items.length === 0;
        if (!items.length) return;
        const params = new URLSearchParams();
        items.forEach((item) => {
            params.append("item_description", item.description);
            params.append("item_quantity", String(item.quantity));
            params.append("item_unit_price", item.unit_price === undefined ? "" : String(item.unit_price));
        });
        this.handoffTarget.href = `${this.quoteUrlValue}?${params}`;
    }

    _syncUrl() {
        const query = new URLSearchParams(new FormData(this.formTarget));
        window.history.replaceState(window.history.state, "", `${this.formTarget.getAttribute("action")}?${query}`);
    }

    /** Show only the boxes that apply to the choice made (a slab has no hole depth). */
    _toggleFields() {
        this.fieldTargets.forEach((field) => {
            if (!field.dataset.showsFor) return;
            const rules = JSON.parse(field.dataset.showsFor);
            field.hidden = Object.entries(rules).some(([key, allowed]) => {
                const control = this.formTarget.elements.namedItem(key);
                return control && !allowed.includes(control.value);
            });
        });
    }

    // --- AI assistant ----------------------------------------------------------

    useExample(event) {
        this.aiInputTarget.value = event.currentTarget.dataset.example;
        this.aiInputTarget.focus();
    }

    aiKeydown(event) {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.askAi();
        }
    }

    async askAi() {
        const instruction = this.aiInputTarget.value.trim();
        if (!instruction || this.aiSendTarget.disabled) return;
        this._say("you", instruction);
        this.aiInputTarget.value = "";
        this._busy(true);
        try {
            const data = await this._postAi(instruction);
            if (data.error) {
                this._say("chip", data.error);
                return;
            }
            this._apply(data.fields || {});
            const reply = data.reply || "Done.";
            this._say("chip", reply);
            this._history.push({ role: "user", content: instruction }, { role: "assistant", content: reply });
        } catch (_error) {
            this._say("chip", "No connection. Fill the boxes in by hand, it still works.");
        } finally {
            this._busy(false);
        }
    }

    async _postAi(instruction) {
        const current = Object.fromEntries(new FormData(this.formTarget));
        const response = await fetch(this.aiUrlValue, {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrfValue },
            body: JSON.stringify({ instruction, session_id: this._session, current,
                                   history: this._history }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok && !data.error) data.error = "That did not go through. Try again in a moment.";
        return data;
    }

    _apply(fields) {
        Object.entries(fields).forEach(([key, value]) => {
            const control = this.formTarget.elements.namedItem(key);
            if (control) control.value = value;
        });
        this.recalculate();
    }

    _busy(busy) {
        this.aiSendTarget.disabled = busy;
        this.aiStatusTarget.textContent = busy ? "Chip is working on it" : "";
    }

    _say(who, text) {
        const line = document.createElement("p");
        line.className = `ai-msg ai-msg--${who}`;
        line.textContent = text;
        this.aiLogTarget.appendChild(line);
        this.aiLogTarget.hidden = false;
        this.aiLogTarget.scrollTop = this.aiLogTarget.scrollHeight;
    }
}
